import { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import FlashcardPreviewModal from "@/components/FlashcardPreviewModal";
import CinematicHero from "@/components/landing/CinematicHero";
import { fadeIn, fadeInUp, fadeInUpSm } from "@/lib/motion-variants";
import { homePathForRole } from "@/lib/role-routes";
import {
  GraduationCap,
  Brain,
  Layers,
  FileText,
  Target,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

const BRAND = "#6C3CE1";

const LandingPage = () => {
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const setsScrollRef = useRef(null);
  const [previewSetId, setPreviewSetId] = useState(null);

  const handleGetStarted = () => {
    if (user) {
      navigate(homePathForRole(user.role));
    } else {
      // Aha-moment flow: 3 questions → personalized plan → THEN sign in
      navigate("/quickstart");
    }
  };

  const handlePreviewSet = (setId) => {
    // Authenticated users skip the preview and go straight to the app.
    if (user) {
      navigate(homePathForRole(user.role));
      return;
    }
    // Anonymous visitors get the in-page preview modal (low friction, no
    // navigation). The same data is also available at /sets/{setId} for
    // direct links / SEO indexing.
    setPreviewSetId(setId);
  };

  const studyModes = [
    { title: "Learn", description: "AI-powered tutoring sessions tailored to your level", icon: Brain, color: "#4257B2" },
    { title: "Flashcards", description: "Create & study flashcards with spaced repetition", icon: Layers, color: "#FF6B35" },
    { title: "Practice Tests", description: "Quiz yourself with AI-generated practice exams", icon: Target, color: "#23B5D3" },
    { title: "Study Guides", description: "Turn notes, slides & videos into study material", icon: FileText, color: "#2ECC71" },
  ];

  const featureSections = [
    {
      title: "Every class, every test, one ultimate study app",
      description: "Create your own flashcards or study AI-generated sets. Study them anytime, anywhere with personalized practice sessions.",
      image: "https://images.pexels.com/photos/9158999/pexels-photo-9158999.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      imageAlt: "Student studying in library",
    },
    {
      title: "Make class material instantly studiable",
      description: "Scan your homework, upload notes, or paste text — our AI transforms it into flashcards, quizzes, and step-by-step solutions in seconds.",
      image: "https://images.unsplash.com/photo-1732209556836-511e26d982da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBsYXB0b3AlMjBtb2Rlcm4lMjBjbGVhbnxlbnwwfHx8fDE3NzY2OTEzNDN8MA&ixlib=rb-4.1.0&q=85",
      imageAlt: "Student using laptop",
      cta: "Try it out",
    },
    {
      title: "Test prep for any subject",
      description: "Memorize anything with personalized practice tests and study sessions. 98% of students say Cognify AI has improved their understanding.",
      image: "https://images.pexels.com/photos/9040286/pexels-photo-9040286.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      imageAlt: "Student learning online",
      cta: "Get started",
      stat: { value: "98%", label: "say it improved their understanding" },
    },
  ];

  const FALLBACK_POPULAR_SETS = [
    { set_id: "fallback-1", title: "AP US History", term_count: 156, curator: "HistoryPro", curator_initial: "H" },
    { set_id: "fallback-2", title: "Biology Chapter 4: Cell Division", term_count: 42, curator: "ScienceNerd", curator_initial: "S" },
    { set_id: "fallback-3", title: "Spanish Verbs - Conjugation", term_count: 88, curator: "LinguaLearn", curator_initial: "L" },
    { set_id: "fallback-4", title: "SAT Math Practice", term_count: 200, curator: "MathWhiz", curator_initial: "M" },
    { set_id: "fallback-5", title: "Chemistry: Periodic Table", term_count: 118, curator: "ChemGuru", curator_initial: "C" },
    { set_id: "fallback-6", title: "English Lit: Shakespeare", term_count: 65, curator: "BookWorm22", curator_initial: "B" },
    { set_id: "fallback-7", title: "World Geography Capitals", term_count: 195, curator: "GeoExplorer", curator_initial: "G" },
    { set_id: "fallback-8", title: "Algebra 2 Formulas", term_count: 74, curator: "NumbersCrunch", curator_initial: "N" },
  ];

  const [popularSets, setPopularSets] = useState(FALLBACK_POPULAR_SETS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/flashcard-sets/popular`);
        if (!cancelled && res.data?.sets?.length > 0) {
          setPopularSets(res.data.sets);
        }
      } catch {
        // Keep fallback list if the endpoint fails — landing page must always render.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scroll = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Public flashcard preview modal — primary acquisition funnel */}
      <FlashcardPreviewModal
        open={!!previewSetId}
        setId={previewSetId}
        onClose={() => setPreviewSetId(null)}
        onUnlock={() => {
          setPreviewSetId(null);
          handleGetStarted();
        }}
      />

      {/* ========== NAVBAR ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border pt-safe" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: BRAND }}>
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>Cognify AI</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LanguageSelector /></div>
            <Button
              variant="ghost"
              className="hidden md:inline-flex text-muted-foreground hover:text-primary font-medium"
              onClick={() => document.getElementById("teachers")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="nav-teachers-btn"
            >
              For Teachers
            </Button>
            <Button
              onClick={handleGetStarted}
              className="text-white font-semibold rounded-lg px-5 h-10 text-sm"
              style={{ background: BRAND }}
              data-testid="nav-get-started-btn"
            >
              {user ? "Dashboard" : "Sign up for free"}
            </Button>
          </div>
        </div>
      </nav>

      {/* ========== HERO ========== */}
      <CinematicHero
        onPrimary={handleGetStarted}
        onGoogle={() => login()}
      />

      {/* ========== STUDY MODES SCROLL ========== */}
      <section className="py-8 sm:py-12 px-4 md:px-8" data-testid="study-modes-section">
        <div className="max-w-7xl mx-auto relative">
          <button onClick={() => scroll(scrollRef, -1)} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 rounded-full bg-white border-2 border-border shadow-md flex items-center justify-center hover:border-primary transition-colors hidden md:flex" data-testid="modes-scroll-left">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <button onClick={() => scroll(scrollRef, 1)} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 rounded-full bg-white border-2 border-border shadow-md flex items-center justify-center hover:border-primary transition-colors hidden md:flex" data-testid="modes-scroll-right">
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
          <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {studyModes.map((mode) => (
              <motion.div
                key={mode.title}
                whileHover={{ y: -4, boxShadow: "0 8px 25px rgba(0,0,0,0.08)" }}
                className="snap-start shrink-0 w-[260px] sm:w-[280px] bg-white border-2 border-border rounded-xl cursor-pointer transition-all"
                style={{ borderTopWidth: "6px", borderTopColor: mode.color }}
                onClick={handleGetStarted}
                data-testid={`mode-${mode.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="p-5">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ background: `${mode.color}15` }}>
                    <mode.icon className="w-5 h-5" style={{ color: mode.color }} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-1" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>{mode.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{mode.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ALTERNATING FEATURE SECTIONS ========== */}
      {featureSections.map((section, index) => (
        <section key={section.title} className={`py-16 md:py-24 px-4 md:px-8 ${index % 2 === 1 ? "bg-secondary/60" : "bg-white"}`} data-testid={`feature-section-${index}`}>
          <div className="max-w-7xl mx-auto">
            <div className={`flex flex-col ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} items-center gap-10 lg:gap-16`}>
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex-1 w-full"
              >
                <img
                  src={section.image}
                  alt={section.imageAlt}
                  loading="lazy"
                  className="w-full h-[280px] sm:h-[340px] lg:h-[380px] object-cover rounded-xl border-2 border-border"
                />
              </motion.div>
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="flex-1 w-full"
              >
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 leading-tight" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                  {section.title}
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                  {section.description}
                </p>
                {section.stat && (
                  <div className="flex items-center gap-3 mb-6 p-4 rounded-lg bg-secondary/60 border border-border">
                    <span className="text-3xl font-bold" style={{ color: BRAND, fontFamily: "'Instrument Serif', Georgia, serif" }}>{section.stat.value}</span>
                    <span className="text-sm text-muted-foreground">{section.stat.label}</span>
                  </div>
                )}
                {section.cta && (
                  <Button
                    onClick={handleGetStarted}
                    className="text-white font-semibold rounded-lg px-6 h-11 text-base hover:scale-[1.02] transition-transform"
                    style={{ background: BRAND }}
                    data-testid={`feature-cta-${index}`}
                  >
                    {section.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* ========== TEACHERS CALLOUT ========== */}
      <section id="teachers" className="py-16 md:py-24 px-4 md:px-8" style={{ background: BRAND }} data-testid="teachers-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex-1"
            >
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-white/60 mb-3">TEACHERS</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4 leading-tight" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                Empower your students
              </h2>
              <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg">
                Help every student confidently learn anything. With free flashcard sets, study modes, and AI-powered insights, you can instantly create a more engaged classroom.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="bg-amber-400 text-foreground font-semibold rounded-lg px-6 h-11 hover:bg-amber-500 hover:scale-[1.02] transition-all"
                  data-testid="teacher-signup-btn"
                >
                  Sign up as a teacher
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white/30 text-white font-medium rounded-lg px-6 h-11 hover:bg-white/10"
                  onClick={() => navigate("/pricing")}
                  data-testid="teacher-learn-more-btn"
                >
                  See pricing for schools
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1 w-full"
            >
              <img
                src="https://images.unsplash.com/photo-1557734864-c78b6dfef1b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx0ZWFjaGVyJTIwY2xhc3Nyb29tJTIwZW5nYWdpbmclMjBzdHVkZW50c3xlbnwwfHx8fDE3NzY2OTEzNDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Teacher engaging with students"
                loading="lazy"
                className="w-full h-[280px] sm:h-[340px] object-cover rounded-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== POPULAR FLASHCARD SETS ========== */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-secondary/60" data-testid="popular-sets-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              Popular flashcard sets
            </h2>
            <div className="hidden sm:flex gap-2">
              <button onClick={() => scroll(setsScrollRef, -1)} className="w-9 h-9 rounded-full border-2 border-border flex items-center justify-center hover:border-primary transition-colors" data-testid="sets-scroll-left">
                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
              </button>
              <button onClick={() => scroll(setsScrollRef, 1)} className="w-9 h-9 rounded-full border-2 border-border flex items-center justify-center hover:border-primary transition-colors" data-testid="sets-scroll-right">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
          <div ref={setsScrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {popularSets.map((set) => (
              <motion.div
                key={set.set_id}
                whileHover={{ y: -3, boxShadow: "0 6px 20px rgba(0,0,0,0.06)" }}
                className="snap-start shrink-0 w-[240px] bg-white border-2 border-border rounded-xl transition-all hover:border-foreground/15"
                data-testid={`set-${set.set_id}`}
              >
                <Link
                  to={`/sets/${set.set_id}`}
                  onClick={(e) => {
                    // Intercept normal click → in-page modal preview.
                    // Middle / Cmd / Ctrl click still opens the SEO page in a
                    // new tab. Crawlers follow the href to index the SEO page.
                    if (e.metaKey || e.ctrlKey || e.button === 1) return;
                    e.preventDefault();
                    handlePreviewSet(set.set_id);
                  }}
                  className="block p-4 cursor-pointer no-underline"
                >
                  <h4 className="text-sm font-semibold text-foreground mb-2 line-clamp-2 leading-snug" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>{set.title}</h4>
                  <p className="text-xs text-muted-foreground mb-3">{set.term_count} terms{set.study_count ? ` · ${set.study_count.toLocaleString()} studies` : ""}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: BRAND }}>
                      {set.curator_initial || set.curator?.charAt(0) || "C"}
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">{set.curator}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              Ready to ace your next test?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join millions of students using Cognify AI to study smarter, not harder. It's free to get started.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="w-full sm:w-auto text-white font-semibold rounded-lg px-8 h-12 text-base hover:scale-[1.02] transition-transform shadow-md"
                style={{ background: BRAND }}
                data-testid="cta-get-started-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                No credit card required
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-8 px-4 md:px-8 border-t border-border bg-white pb-safe" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: BRAND }}>
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-foreground" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>Cognify AI</span>
          </div>
          <p className="text-muted-foreground text-sm">
            &copy; 2025 Cognify AI by ServeNetics. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-muted-foreground hover:text-primary text-sm transition-colors">Privacy</a>
            <a href="#" className="text-muted-foreground hover:text-primary text-sm transition-colors">Terms</a>
            <a href="#" className="text-muted-foreground hover:text-primary text-sm transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
